import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import '../Assets/Stylesheets/Components/MarketingForecast.scss';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function SocialMediaForecast({ forecastData }) {
  if (!forecastData) return null;

  const { engagement_rate, followers, reach, total_engagement, assumptions, summary } = forecastData;

  // Chart configurations
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#FE9700',
        bodyColor: '#fff',
        borderColor: 'rgba(254, 151, 0, 0.3)',
        borderWidth: 1,
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.05)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: 11
          }
        }
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.05)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: 11
          }
        }
      }
    }
  };

  // Engagement Rate Chart Data
  const engagementRateChartData = {
    labels: MONTHS,
    datasets: [
      {
        label: 'Engagement Rate (%)',
        data: engagement_rate,
        borderColor: '#FE9700',
        backgroundColor: 'rgba(254, 151, 0, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: '#FE9700',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }
    ]
  };

  // Follower Growth Chart Data
  const followersChartData = {
    labels: MONTHS,
    datasets: [
      {
        label: 'New Followers',
        data: followers,
        backgroundColor: 'rgba(255, 170, 1, 0.8)',
        borderColor: '#FFAA01',
        borderWidth: 1,
      }
    ]
  };

  // Reach Chart Data
  const reachChartData = {
    labels: MONTHS,
    datasets: [
      {
        label: 'Reach/Impressions',
        data: reach,
        borderColor: '#FFAA01',
        backgroundColor: 'rgba(255, 170, 1, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: '#FFAA01',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }
    ]
  };

  // Total Engagement Chart Data
  const totalEngagementChartData = {
    labels: MONTHS,
    datasets: [
      {
        label: 'Total Engagement',
        data: total_engagement,
        borderColor: '#FF6300',
        backgroundColor: 'rgba(255, 99, 0, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: '#FF6300',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }
    ]
  };

  const formatNumber = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return new Intl.NumberFormat('en-US').format(value);
  };

  const totalFollowers = followers.reduce((a, b) => a + b, 0);
  const totalReach = reach.reduce((a, b) => a + b, 0);

  return (
    <div className="forecast-container">
      <div className="forecast-header">
        <h3 className="forecast-title">📱 1-Year Social Media Growth Forecast</h3>
        {summary && <p className="forecast-summary">{summary}</p>}
      </div>

      {/* Charts Grid */}
      <div className="forecast-charts-grid">
        {/* Engagement Rate Chart */}
        <div className="forecast-chart-card">
          <div className="chart-header">
            <h4 className="chart-title">Engagement Rate</h4>
            <div className="chart-stat">
              <span className="stat-label">Year End:</span>
              <span className="stat-value stat-roi">{engagement_rate[11]}%</span>
            </div>
          </div>
          <div className="chart-wrapper">
            <Line data={engagementRateChartData} options={chartOptions} />
          </div>
        </div>

        {/* Follower Growth Chart */}
        <div className="forecast-chart-card">
          <div className="chart-header">
            <h4 className="chart-title">Follower Growth</h4>
            <div className="chart-stat">
              <span className="stat-label">Total Year:</span>
              <span className="stat-value stat-leads">{formatNumber(totalFollowers)}</span>
            </div>
          </div>
          <div className="chart-wrapper">
            <Bar data={followersChartData} options={chartOptions} />
          </div>
        </div>

        {/* Reach/Impressions Chart */}
        <div className="forecast-chart-card">
          <div className="chart-header">
            <h4 className="chart-title">Reach & Impressions</h4>
            <div className="chart-stat">
              <span className="stat-label">Total Year:</span>
              <span className="stat-value stat-revenue">{formatNumber(totalReach)}</span>
            </div>
          </div>
          <div className="chart-wrapper">
            <Line data={reachChartData} options={chartOptions} />
          </div>
        </div>

        {/* Total Engagement Chart */}
        <div className="forecast-chart-card">
          <div className="chart-header">
            <h4 className="chart-title">Total Engagement</h4>
            <div className="chart-stat">
              <span className="stat-label">Year End:</span>
              <span className="stat-value stat-cac">{formatNumber(total_engagement[11])}/mo</span>
            </div>
          </div>
          <div className="chart-wrapper">
            <Line data={totalEngagementChartData} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* Assumptions Section */}
      <div className="forecast-assumptions">
        <h4 className="assumptions-title">⚠️ Key Assumptions</h4>
        <p className="assumptions-disclaimer">
          This forecast is 100% dependent on the following assumptions. Actual results may vary based on content quality, posting consistency, and platform algorithm changes.
        </p>
        <ul className="assumptions-list">
          {assumptions.map((assumption, index) => (
            <li key={index} className="assumption-item">
              <span className="assumption-bullet">{index + 1}</span>
              <span className="assumption-text">{assumption}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

